import { useState, useContext } from "react";
import { Form, Input, Button, Modal } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { UserDataContext } from "../context/User";

// ── Reusable inline alert ─────────────────────────────────────────────────────
const Alert = ({ type, message }) => {
  if (!message) return null;
  const styles = {
    error: { bg: "#fff1f0", border: "#ffa39e", color: "#cf1322", icon: "⚠" },
    success: { bg: "#f6ffed", border: "#b7eb8f", color: "#389e0d", icon: "✓" },
  };
  const s = styles[type] || styles.error;
  return (
    <div style={{
      marginBottom: 16,
      padding: "12px 16px",
      borderRadius: 10,
      backgroundColor: s.bg,
      border: `1px solid ${s.border}`,
      color: s.color,
      fontFamily: "'Outfit', sans-serif",
      fontSize: 14,
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
    }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>{s.icon}</span>
      <span>{message}</span>
    </div>
  );
};

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { login, setUserData } = useContext(UserDataContext);

  const [loginError, setLoginError] = useState("");

  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordForm] = Form.useForm();
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  const handleLogin = async (values) => {
    setLoading(true);
    setLoginError("");
    try {
      const response = await login({
        emailOrMemberId: values.emailOrMemberId,
        password: values.password,
        from: "web",
      });

      if (response?.success) {
        setUserData(response.user || null);
        form.resetFields();
        navigate(from, { replace: true });
      } else {
        setLoginError(response?.message || "Login failed. Please check your credentials.");
      }
    } catch (err) {
      setLoginError(
        err?.response?.data?.message || err?.message || "Login failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (values) => {
    setForgotPasswordLoading(true);
    setForgotError("");
    setForgotSuccess("");
    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: values.email, origin: window.location.origin }),
      });
      const data = await response.json();

      if (response.ok) {
        setForgotSuccess("Password reset link sent! Check your email inbox.");
        forgotPasswordForm.resetFields();
      } else {
        setForgotError(data.message || "Failed to send reset link. Please try again.");
      }
    } catch {
      setForgotError("Something went wrong. Please try again.");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const inputStyle = {
    background: "rgba(255,255,255,0.05)",
    color: "#333",
    border: "1px solid #999",
    borderRadius: "12px",
    padding: "12px 16px",
    fontSize: "15px",
    height: "48px",
    transition: "all 0.3s ease",
  };

  const labelStyle = {
    fontFamily: "'Outfit', sans-serif",
    fontSize: "15px",
    fontWeight: "500",
    color: "black",
    letterSpacing: "0.5px",
  };

  return (
    <>
      <div
        className="login-container"
        style={{
          minHeight: "100vh",
          background: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            maxWidth: "520px",
            width: "100%",
            background: "linear-gradient(29deg, rgba(0,240,251,1) 0%, rgba(255,255,255,1) 50%, rgba(0,240,251,1) 100%)",
            borderRadius: "20px",
            padding: "40px 30px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <div style={{ width: "60px", height: "4px", background: "black", margin: "0 auto 20px", borderRadius: "2px" }} />
            <h3 style={{ color: "black", fontFamily: "'Outfit', sans-serif", fontSize: "clamp(12px,3vw,14px)", fontWeight: "300", lineHeight: "1.6", margin: "0 auto 15px", maxWidth: "90%", opacity: 0.9 }}>
              Log in to your account and reignite your creative passions
            </h3>
            <h1 className="font-cinzel" style={{ color: "black", fontSize: "clamp(24px,5vw,32px)", fontWeight: "500", margin: "0" }}>
              WELCOME BACK
            </h1>
          </div>

          {/* Inline login error */}
          <Alert type="error" message={loginError} />

          <Form form={form} layout="vertical" onFinish={handleLogin} requiredMark={false}>
            <Form.Item
              label={<span style={labelStyle}>Email or Member ID</span>}
              name="emailOrMemberId"
              rules={[{ required: true, message: "Please enter your email or member ID" }]}
            >
              <Input
                size="large"
                style={inputStyle}
                placeholder="Enter your email or member ID"
                onChange={() => setLoginError("")}
              />
            </Form.Item>

            <Form.Item
              label={<span style={labelStyle}>Password</span>}
              name="password"
              rules={[
                { required: true, message: "Please enter your password" },
                { min: 6, message: "Password must be at least 6 characters" },
              ]}
            >
              <Input.Password
                size="large"
                style={inputStyle}
                placeholder="Enter your password"
                onChange={() => setLoginError("")}
              />
            </Form.Item>

            <div style={{ textAlign: "right", marginBottom: "20px" }}>
              <Button
                type="link"
                onClick={() => { setForgotPasswordModal(true); setForgotError(""); setForgotSuccess(""); }}
                style={{ color: "black", padding: 0, fontSize: "14px", fontWeight: "500", textDecoration: "underline" }}
              >
                Forgot Password?
              </Button>
            </div>

            <Form.Item style={{ marginBottom: "20px" }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="font-cinzel"
                style={{ background: "black", color: "#fff", width: "100%", borderRadius: "12px", height: "50px", fontSize: "16px", fontWeight: "500", border: "none" }}
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Modal
        title={<span style={{ fontFamily: "'Cinzel', serif", fontSize: "20px", fontWeight: "500" }}>Reset Password</span>}
        open={forgotPasswordModal}
        onCancel={() => { setForgotPasswordModal(false); setForgotError(""); setForgotSuccess(""); forgotPasswordForm.resetFields(); }}
        footer={null}
        centered
      >
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "14px", color: "#666", marginBottom: "16px" }}>
          Enter your email address and we'll send you a link to reset your password.
        </p>

        <Alert type="error" message={forgotError} />
        <Alert type="success" message={forgotSuccess} />

        {!forgotSuccess && (
          <Form form={forgotPasswordForm} onFinish={handleForgotPassword} layout="vertical">
            <Form.Item
              label={<span style={{ fontFamily: "'Outfit', sans-serif", fontSize: "14px", fontWeight: "500" }}>Email Address</span>}
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input size="large" placeholder="Enter your email address" style={{ borderRadius: "8px", height: "45px" }} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={forgotPasswordLoading}
                style={{ background: "black", width: "100%", height: "45px", borderRadius: "8px", fontSize: "15px", fontWeight: "500" }}
              >
                {forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
}

export default Login;
